/**
 * Componente Header
 *
 * Barra superior da aplicação com controles do usuário.
 *
 * Funcionalidades:
 * - Botão de menu para mobile
 * - Toggle de tema claro/escuro
 * - Notificações
 * - Menu do usuário
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, Sun, Moon, User, LogOut, Settings, BookOpen, Smartphone } from 'lucide-react';
import { USER_MANUAL_HINT, USER_MANUAL_URL } from '@utils/userManual';
import { useAuth } from '@hooks/useAuth';
import { useTheme } from '@contexts/ThemeContext';
import { useNotificationStore } from '@store/useNotificationStore';
import { NotificationPanel } from '@components/shared/NotificationPanel';
import { clsx } from 'clsx';
import { promptInstall, useInstallPrompt } from '@utils/pwaInstall';
import { InstallInstructionsModal } from '@components/pwa/InstallInstructions';

/**
 * Props do componente Header
 */
interface HeaderProps {
  /** Callback para abrir menu mobile */
  onMenuClick: () => void;
}

/**
 * Componente Header
 */
export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { unreadCount } = useNotificationStore();
  const navigate = useNavigate();

  // Estado do menu dropdown do usuário
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  // Instalação manual do app do painel (#291): sem aviso automático. A opção aparece
  // sempre que o painel não está aberto como app; sem o pedido do navegador, explica o
  // caminho pelo menu (Android/computador) ou por Compartilhar (iPhone)
  const install = useInstallPrompt();
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  // Estado do painel de notificações
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);

  return (
    <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-800">
      {/* Lado esquerdo - botão de menu */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 lg:hidden"
          aria-label="Abrir menu"
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Lado direito - controles */}
      <div className="flex items-center gap-2">
        {/* Toggle de tema */}
        <button
          onClick={toggleTheme}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
          aria-label={isDark ? 'Ativar tema claro' : 'Ativar tema escuro'}
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* Notificações */}
        <div className="relative">
          <button
            onClick={() => setIsNotificationPanelOpen(!isNotificationPanelOpen)}
            className="relative rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            aria-label="Notificações"
          >
            <Bell size={20} />
            {/* Badge de notificação */}
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-error-500 text-[10px] font-bold text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Painel de notificações */}
          <NotificationPanel
            isOpen={isNotificationPanelOpen}
            onClose={() => setIsNotificationPanelOpen(false)}
          />
        </div>

        {/* Divider */}
        <div className="mx-2 h-6 w-px bg-gray-200 dark:bg-gray-700" />

        {/* Menu do usuário */}
        <div className="relative">
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-2 rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {/* Avatar */}
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-800 text-white dark:bg-secondary-700">
              <span className="text-sm font-medium">
                {user?.name.charAt(0).toUpperCase()}
              </span>
            </div>

            {/* Nome (oculto em mobile) */}
            <span className="hidden text-sm font-medium text-gray-700 dark:text-gray-200 md:block">
              {user?.name}
            </span>
          </button>

          {/* Dropdown menu */}
          {isUserMenuOpen && (
            <>
              {/* Overlay para fechar ao clicar fora */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsUserMenuOpen(false)}
              />

              {/* Menu */}
              <div className="absolute right-0 z-20 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {/* Info do usuário */}
                <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email}
                  </p>
                </div>

                {/* Opções */}
                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/configuracoes');
                    }}
                    className={clsx(
                      'flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700',
                      'hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700',
                    )}
                  >
                    <User size={16} />
                    Meu perfil
                  </button>

                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/configuracoes');
                    }}
                    className={clsx(
                      'flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700',
                      'hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700',
                    )}
                  >
                    <Settings size={16} />
                    Configurações
                  </button>

                  {!install.installed && (
                    <button
                      onClick={async () => {
                        setIsUserMenuOpen(false);
                        if (!install.canPrompt) {
                          setShowInstallHelp(true);
                          return;
                        }
                        if ((await promptInstall()) === 'unavailable') setShowInstallHelp(true);
                      }}
                      className={clsx(
                        'flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700',
                        'hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700',
                      )}
                    >
                      <Smartphone size={16} />
                      Instalar o app Vende Aí
                    </button>
                  )}

                  {USER_MANUAL_URL && (
                    <a
                      href={USER_MANUAL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsUserMenuOpen(false)}
                      className={clsx(
                        'flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700',
                        'hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700',
                      )}
                      aria-label={`Manual do usuário (${USER_MANUAL_HINT}, abre em nova aba)`}
                    >
                      <BookOpen size={16} />
                      <span className="flex flex-col text-left leading-tight">
                        <span>Manual do usuário</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{USER_MANUAL_HINT}</span>
                      </span>
                    </a>
                  )}
                </div>

                {/* Logout */}
                <div className="border-t border-gray-200 py-1 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      logout();
                    }}
                    className={clsx(
                      'flex w-full items-center gap-2 px-4 py-2 text-sm text-error-600',
                      'hover:bg-error-50 dark:text-error-400 dark:hover:bg-error-900/20',
                    )}
                  >
                    <LogOut size={16} />
                    Sair
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <InstallInstructionsModal
        isOpen={showInstallHelp}
        onClose={() => setShowInstallHelp(false)}
        appName="Vende Aí"
      />
    </header>
  );
}
